import { useRef, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import paymentService, { UTR_PATTERN } from '../../../services/paymentService';
import { getErrorMessage } from '../../../utils/apiError';

/**
 * The buyer enters the UPI transaction ID (UTR) after paying.
 * onSubmitted(result) receives { outcome, paymentState, orderState } from the server.
 */
const PaymentProofForm = ({ paymentId, onSubmitted }) => {
  const [utr, setUtr] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  const handleSubmit = async event => {
    event.preventDefault();
    if (submitting) return;

    setSubmitError('');
    if (!utr) {
      setFieldError('Enter the UTR from your UPI app.');
      inputRef.current?.focus();
      return;
    }
    if (!UTR_PATTERN.test(utr)) {
      setFieldError('Enter the UTR exactly as shown in your UPI app: 10 to 22 letters or digits.');
      inputRef.current?.focus();
      return;
    }

    setFieldError('');
    setSubmitting(true);
    try {
      const result = await paymentService.submitPaymentProof(paymentId, utr);
      onSubmitted(result || {});
    } catch (error) {
      setSubmitError(getErrorMessage(error, "We couldn't submit your payment. Please try again."));
      setSubmitting(false);
    }
  };

  const describedBy = ['co-utr-hint', fieldError && 'co-utr-error'].filter(Boolean).join(' ');

  return (
    <section className="pk-card" aria-labelledby="co-proof-title">
      <h2 className="pk-card__title co-card-title" id="co-proof-title">
        Submit your payment
      </h2>
      <form className="pk-form" onSubmit={handleSubmit} noValidate>
        <div className="pk-field">
          <label className="pk-label" htmlFor="co-utr">
            UPI transaction ID (UTR)
          </label>
          <input
            id="co-utr"
            ref={inputRef}
            className="pk-input co-mono"
            inputMode="numeric"
            maxLength={22}
            autoComplete="off"
            spellCheck={false}
            value={utr}
            required
            aria-invalid={fieldError ? 'true' : undefined}
            aria-describedby={describedBy}
            onChange={e => {
              // UPI apps often group the digits; spaces aren't part of the UTR.
              setUtr(e.target.value.replace(/\s+/g, ''));
              if (fieldError) setFieldError('');
            }}
          />
          <p className="pk-hint" id="co-utr-hint">
            12-digit UTR from your UPI app
          </p>
          {fieldError && (
            <p className="pk-error" id="co-utr-error">
              {fieldError}
            </p>
          )}
        </div>

        {submitError && (
          <div className="pk-alert" data-tone="danger" role="alert">
            <AlertCircle aria-hidden="true" />
            <span>{submitError}</span>
          </div>
        )}

        <button
          type="submit"
          className="pk-btn pk-btn--primary pk-btn--block"
          aria-busy={submitting ? 'true' : undefined}
        >
          Submit payment
        </button>
      </form>
    </section>
  );
};

export default PaymentProofForm;
